import { type Context, type ReactNode, createContext, useContext, useMemo } from "react";
import type { useDRP } from "./logics";

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const DRPContext = createContext<ReturnType<typeof useDRP> | undefined>(undefined) as any;

export const useDRPContext = <Chip extends string = string>() => {
  const context = useContext<ReturnType<typeof useDRP<Chip>> | undefined>(
    DRPContext as Context<ReturnType<typeof useDRP<Chip>> | undefined>
  );
  if (context === undefined) {
    throw new Error("useDRPContext must be within DateRangePicker");
  }

  return context;
};

type Idrm<Chip extends string = string> = ReturnType<typeof useDRP<Chip>> & {
  children: ReactNode;
};

export const DRPProvider = <Chip extends string = string>({ children, ...props }: Idrm<Chip>) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const value = useMemo(
    () => ({
      ...props,
    }),
    [
      props.inputValue,
      props.innerChips,
      props.range,
      props.handleRangeChange,
      props.setRangeWithChange,
      props.open,
      props.anchorEl,
      props.setAnchorEl,
      props.selectedChip,
      props.setSelectedChip,
      props.selectedView,
      props.setSelectedView,
      props.handleClear,
      props.handleClose,
      props.views,
      props.handleChipChange,
      props.error,
      props.setError,
      props.required,
      props.endDateDisabled,
      props.timeZone,
    ]
  );

  return <DRPContext.Provider value={value}>{children}</DRPContext.Provider>;
};
